import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  IconButton,
  TextField,
  useAutocomplete
} from '@mui/material';
import React, { useCallback } from 'react';
import InfiniteListWrapper from './InfiniteListWrapper';
import { useStateReducer } from '../../hooks';
import { paginate } from '../../utilities/arrayUtils';
import { debounce } from '../../utilities/perfomanceUtils';
import isObject from '../../utilities/isObject';
import { classNameBuilder } from '../../utilities';
import { ClearIcon } from '@mui/x-date-pickers';

interface IInfiniteListProps {
  label?: string;
  error?: boolean;
  getOptionValue?(option: any): any;
  value?: any;
  className?: string;
  id?: string;
  onChange?: (
    event: React.SyntheticEvent<Element, Event>,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<any> | undefined
  ) => void;
  onClear?: () => void;
  disabled?: boolean;
  readOnly?: boolean;
  options: any[];
  getOptionDisabled?: (option: any) => boolean;
  getOptionLabel?: (option: any) => string;
  getOptionValue?: (option: any) => any;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  multiple?: boolean;
  groupBy?: (option: any) => string;
}

export default function InfiniteList({
  label,
  value,
  className,
  onChange,
  disabled,
  readOnly,
  options,
  getOptionDisabled,
  getOptionLabel,
  getOptionValue,
  isOptionEqualToValue,
  multiple,
  groupBy,
  error,
  onClear
}: IInfiniteListProps) {
  const PAGE_SIZE = 25;
  const TOTAL_PAGES = Math.ceil([...options].length / PAGE_SIZE);
  const PAGE_NUMBER = 1;
  const DEBOUNCE_DELAY = 650;

  const [state, setState] = useStateReducer({
    partnerLinks: paginate(options, PAGE_SIZE, PAGE_NUMBER),
    isLoading: false,
    searchList: [...options],
    hasNextPage: true,
    pageNumber: PAGE_NUMBER,
    setValue: value
  });

  let val;
  if (!value) val = null;
  else if (isObject(value)) val = value;
  else if (getOptionValue)
    val = state.searchList.filter((o: any) => getOptionValue(o) === value);

  const delayOnLoadItems = async () =>
    new Promise((resolve) => setTimeout(resolve, 950));

  const onScrollToTheBottom = async () => {
    if (!state.hasNextPage || state.loading) return;

    setState({
      isLoading: true
    });

    await delayOnLoadItems();
    let newLinks = paginate(options, PAGE_SIZE, PAGE_NUMBER);

    setState({
      isLoading: false,
      pageNumber: state.pageNumber + 1,
      hasNextPage: state.pageNumber < TOTAL_PAGES,
      partnerLinks: [...state.partnerLinks, ...newLinks]
    });
  };
  const debounceOnScroll = useCallback(
    debounce(onScrollToTheBottom, DEBOUNCE_DELAY),
    [onScrollToTheBottom]
  );

  const {
    getRootProps,
    getInputLabelProps,
    getListboxProps,
    getOptionProps,
    groupedOptions,
    getInputProps,
    setAnchorEl
  } = useAutocomplete({
    id: 'use-autocomplete-demo',
    options: options,
    getOptionDisabled: getOptionDisabled,
    isOptionEqualToValue: (o, v) => v && isOptionEqualToValue?.(o, v),
    multiple: multiple,
    disabled: disabled,
    groupBy: groupBy,
    readOnly: readOnly,
    getOptionLabel: getOptionLabel,
    onChange: onChange,
    value: value
  });

  return (
    <div
      className={classNameBuilder(
        'h-app-autocomplete-field',
        readOnly ? 'readonly' : '',
        className ?? ''
      )}
    >
      <div {...getRootProps()}>
        <TextField
          ref={setAnchorEl}
          InputLabelProps={getInputLabelProps()}
          label={label}
          fullWidth
          error={error}
          size='small'
          value={state.value}
          inputProps={getInputProps()} //	Attributes applied to the input element.
          InputProps={{
            // Props applied to the Input element.
            // It will be a FilledInput, OutlinedInput or Input component
            // depending on the variant prop value.
            endAdornment: val && (
              <IconButton onClick={onClear}>
                <ClearIcon />
              </IconButton>
            )
          }}
        />
      </div>
      {groupedOptions.length > 0 ? (
        <InfiniteListWrapper
          hasNextPage={state.hasNextPage}
          items={state.partnerLinks}
          loadNextPage={debounceOnScroll}
          getOptionProps={getOptionProps}
          groupedOptions={groupedOptions}
          listboxProps={getListboxProps()}
        />
      ) : null}
    </div>
  );
}
